import { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { TechStackIcon } from '../../components/TechStackIcon';
import { RichTextEditor } from '../../components/RichTextEditor';
import { supabase, sanitizeInput, sanitizeHtml, sanitizeUrl } from '../../lib/supabase';
import type { Project } from '../../types';
import { techStackOptions, projectCategories, currentAppTechStack } from '../../config/techStack';
import {
  ArrowLeft,
  Save,
  Upload,
  Plus,
  X,
  Image as ImageIcon,
  Link as LinkIcon,
  Star,
  Target,
  Lightbulb,
  Rocket,
  TrendingUp,
  CheckCircle,
  Search,
  Clock
} from 'lucide-react';

export function ProjectEditor() {
  const navigate = useNavigate();
  const { id } = useParams();
  // Removed useProjects - using direct Supabase queries
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Form state
  const [formData, setFormData] = useState<Partial<Project>>({
    title: '',
    slug: '',
    description: '',
    category: 'apps',
    featured_image: null,
    tech_stack: [],
    demo_url: '',
    status: 'concept',
    completion_percentage: 0,
    is_featured: false,
    key_features: [],
    meta_title: '',
    meta_description: '',
    gallery_images: [],
    metrics: {}
  });

  const [showTechStackDropdown, setShowTechStackDropdown] = useState(false);
  const [techStackSearch, setTechStackSearch] = useState('');
  const [newFeature, setNewFeature] = useState({ name: '', status: 'planned' });
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [galleryPreviews, setGalleryPreviews] = useState<string[]>([]);
  
  const imageInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const techStackRef = useRef<HTMLDivElement>(null);

  // Load existing project data if editing
  useEffect(() => {
    const loadProject = async () => {
      if (id && id !== 'new') {
        try {
          const { data: project, error } = await supabase
            .from('projects')
            .select('*')
            .eq('id', id)
            .single();
            
          if (error) {
            console.error('Error loading project:', error);
            return;
          }
          
          if (project) {
            // Map database fields to form data structure
            const mappedProject: Partial<Project> = {
              id: project.id,
              title: project.title || '',
              slug: project.slug || undefined,
              description: project.description || '',
              category: project.category || 'apps',
              featured_image: project.featured_image || null,
              tech_stack: project.tech_stack || [],
              demo_url: project.demo_url || undefined,
              status: (project.status || 'concept') as Project['status'],
              completion_percentage: project.completion_percentage ?? 0,
              is_featured: project.featured || false,
              key_features: project.key_features?.map(f => {
                if (typeof f === 'string') {
                  // Check if it contains status information (format: "name|status")
                  if (f.includes('|')) {
                    const [name, status] = f.split('|');
                    return { name, description: '', status: status as 'completed' | 'in-progress' | 'planned' };
                  }
                  // Legacy format - just the name
                  return { name: f, description: '', status: 'completed' as const };
                }
                return f;
              }) || [],
              meta_title: project.meta_title || '',
              meta_description: project.meta_description || '',
              gallery_images: project.gallery_images || [],
              // Load metrics from testimonials field (where we store them)
              metrics: (project.testimonials as any) || {},
              created_at: project.created_at || undefined,
              updated_at: project.updated_at || undefined
            };
            setFormData(mappedProject);
            setImagePreview(project.featured_image);
            setGalleryPreviews(project.gallery_images || []);
          }
        } catch (error) {
          console.error('Error loading project:', error);
        }
      } else if (id === 'new') {
        // Pre-populate with current app tech stack for new projects
        const defaultTechStack = currentAppTechStack
          .map(techId => techStackOptions.find(opt => opt.id === techId)?.name)
          .filter(Boolean) as string[];
        setFormData(prev => ({ ...prev, tech_stack: defaultTechStack }));
      }
    };

    loadProject();
  }, [id]);

  // Close tech stack dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (techStackRef.current && !techStackRef.current.contains(event.target as Node)) {
        setShowTechStackDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const statusOptions = [
    { value: 'concept', label: 'Concept', icon: Lightbulb, color: 'purple' },
    { value: 'prototype', label: 'Prototype', icon: Target, color: 'blue' },
    { value: 'mvp', label: 'MVP', icon: Rocket, color: 'orange' },
    { value: 'launched', label: 'Launched', icon: CheckCircle, color: 'green' },
    { value: 'scaling', label: 'Scaling', icon: TrendingUp, color: 'emerald' }
  ];

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsLoading(true);
    setError(null);
    
    try {
      // Validate file
      // File validation

      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        throw new Error('File size must be less than 10MB');
      }

      if (!file.type.startsWith('image/')) {
        throw new Error('Please select an image file');
      }

      // Create a preview
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target?.result as string);
      reader.readAsDataURL(file);

      // Removed storage debug - file was deleted

      // Try different upload strategies
      const fileExt = file.name.split('.').pop();
      const timestamp = Date.now();
      
      // Strategy 1: Simple filename in root
      // Try simple filename upload
      const simpleFileName = `upload_${timestamp}.${fileExt}`;
      
      const { error: uploadError1 } = await supabase.storage
        .from('project-images')
        .upload(simpleFileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (!uploadError1) {
        // Upload successful
        const { data: { publicUrl } } = supabase.storage
          .from('project-images')
          .getPublicUrl(simpleFileName);
        setFormData(prev => ({ ...prev, featured_image: publicUrl }));
        return;
      }

      // Strategy 1 failed, trying alternative

      // Strategy 2: Try with different file name format
      // Try alternative naming strategy
      const altFileName = `${timestamp}_${Math.random().toString(36).substring(7)}.${fileExt}`;
      
      const { error: uploadError2 } = await supabase.storage
        .from('project-images')
        .upload(altFileName, file);

      if (!uploadError2) {
        // Upload successful with alternative naming
        const { data: { publicUrl } } = supabase.storage
          .from('project-images')
          .getPublicUrl(altFileName);
        setFormData(prev => ({ ...prev, featured_image: publicUrl }));
        return;
      }

      // Strategy 2 failed, trying base64

      // Strategy 3: Try with base64 encoding (as last resort)
      // Try base64 upload approach
      const base64FileName = `b64_${timestamp}.${fileExt}`;
      
      // Convert to base64 and try upload
      const arrayBuffer = await file.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);
      
      const { error: uploadError3 } = await supabase.storage
        .from('project-images')
        .upload(base64FileName, uint8Array, {
          contentType: file.type,
          cacheControl: '3600'
        });

      if (!uploadError3) {
        // Base64 upload successful
        const { data: { publicUrl } } = supabase.storage
          .from('project-images')
          .getPublicUrl(base64FileName);
        setFormData(prev => ({ ...prev, featured_image: publicUrl }));
        return;
      }

      console.error('All upload strategies failed:', {
        error1: uploadError1,
        error2: uploadError2,
        error3: uploadError3
      });

      // If all strategies fail, provide detailed error
      throw new Error(`Upload failed: ${uploadError1?.message || 'Unknown storage error'}. Please check your Supabase storage configuration.`);

    } catch (error) {
      console.error('Error uploading image:', error);
      setError(error instanceof Error ? error.message : 'Failed to upload image');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGalleryUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    setIsLoading(true);
    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        const fileExt = file.name.split('.').pop();
        const fileName = `projects/gallery/${Date.now()}-${Math.random()}.${fileExt}`;
        
        const { error } = await supabase.storage
          .from('project-images')
          .upload(fileName, file);

        if (error) throw error;

        const { data: { publicUrl } } = supabase.storage
          .from('project-images')
          .getPublicUrl(fileName);

        return publicUrl;
      });

      const urls = await Promise.all(uploadPromises);
      setGalleryPreviews(prev => [...prev, ...urls]);
      setFormData(prev => ({ 
        ...prev, 
        gallery_images: [...(prev.gallery_images || []), ...urls] 
      }));
    } catch (error) {
      console.error('Error uploading gallery images:', error);
      setError('Failed to upload gallery images');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);

    try {
      // Prepare form data for save
      
      // Prepare project data for database - only include fields that exist in the database schema
      const projectData = {
        title: sanitizeInput(formData.title || ''),
        slug: sanitizeInput(formData.slug || ''),
        description: sanitizeHtml(formData.description || ''),
        category: sanitizeInput(formData.category || 'apps'),
        featured_image: sanitizeUrl(formData.featured_image || ''),
        tech_stack: (formData.tech_stack || []).map(tech => sanitizeInput(tech)),
        demo_url: sanitizeUrl(formData.demo_url || ''),
        status: sanitizeInput(formData.status || 'concept'),
        completion_percentage: formData.completion_percentage ?? 0,
        featured: formData.is_featured || false,
        key_features: (formData.key_features || []).map(f => 
          typeof f === 'string' ? sanitizeInput(f) : `${sanitizeInput(f.name)}|${sanitizeInput(f.status)}`
        ),
        meta_title: sanitizeInput(formData.meta_title || ''),
        meta_description: sanitizeInput(formData.meta_description || ''),
        gallery_images: (formData.gallery_images || []).map(img => sanitizeUrl(img)),
        // Store metrics as JSON in the testimonials field (since it's a JSON field)
        testimonials: formData.metrics || {},
        updated_at: new Date().toISOString()
      };

      let result;
      if (id && id !== 'new') {
        result = await supabase
          .from('projects')
          .update(projectData)
          .eq('id', id)
          .select();
        
        if (result.error) {
          throw result.error;
        }
      } else {
        result = await supabase
          .from('projects')
          .insert([projectData])
          .select();
        
        if (result.error) {
          throw result.error;
        }
      }

      // Show success message
      alert('Project saved successfully!');
      navigate('/admin/projects');
    } catch (error: any) {
      console.error('Error saving project:', error);
      const errorMessage = error?.message || error?.details || 'Failed to save project';
      setError(`Save failed: ${errorMessage}`);
      alert(`Error saving project: ${errorMessage}`);
    } finally {
      setIsSaving(false);
    }
  };

  const addTechStack = (tech: string) => {
    if (!formData.tech_stack?.includes(tech)) {
      setFormData(prev => ({
        ...prev,
        tech_stack: [...(prev.tech_stack || []), tech]
      }));
    }
    setShowTechStackDropdown(false);
    setTechStackSearch('');
  };

  const removeTechStack = (index: number) => {
    setFormData(prev => ({
      ...prev,
      tech_stack: prev.tech_stack?.filter((_, i) => i !== index) || []
    }));
  };


  const addFeature = () => {
    if (newFeature.name.trim()) {
      setFormData(prev => ({
        ...prev,
        key_features: [...(prev.key_features || []), { 
          name: newFeature.name.trim(), 
          description: '',
          status: newFeature.status as 'completed' | 'in-progress' | 'planned'
        }]
      }));
      // Reset to planned status for next feature
      setNewFeature({ name: '', status: 'planned' });
    }
  };

  const removeFeature = (index: number) => {
    setFormData(prev => ({
      ...prev,
      key_features: prev.key_features?.filter((_, i) => i !== index) || []
    }));
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900"
    >
      {/* Header */}
      <div className="sticky top-0 z-30 bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl border-b border-gray-200/50 dark:border-gray-700/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin/projects')}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {id === 'new' ? 'Create New Project' : 'Edit Project'}
              </h1>
            </div>
            <button
              type="submit"
              form="project-form"
              disabled={isSaving || isLoading}
              className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-xl hover:shadow-lg hover:scale-[1.02] transition-all duration-200 font-medium disabled:opacity-50"
            >
              <Save className="w-5 h-5" />
              {isSaving ? 'Saving...' : 'Save Project'}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form id="project-form" onSubmit={handleSubmit} className="space-y-8">
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-4 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-xl"
            >
              {error}
            </motion.div>
          )}

          {/* Basic Information */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl border border-gray-200/50 dark:border-gray-700/50 shadow-xl p-8"
          >
            <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">
              Basic Information
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Project Title *
                </label>
                <input
                  type="text"
                  value={formData.title || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white/50 dark:bg-gray-700/50 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                  required
                />
              </div>

              {/* Project Slug */}
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Project Slug
                  <span className="text-xs text-gray-500 ml-2">(URL-friendly version of title)</span>
                </label>
                <input
                  type="text"
                  value={formData.slug || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                  placeholder="e.g., tabletalk-app"
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white/50 dark:bg-gray-700/50 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Leave empty to auto-generate from title
                </p>
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description *
                </label>
                <div className="min-h-[200px]">
                  <RichTextEditor
                    content={formData.description || ''}
                    onChange={(description) => setFormData(prev => ({ ...prev, description }))}
                    placeholder="Describe your project..."
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Category *
                </label>
                <select
                  value={formData.category || 'apps'}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white/50 dark:bg-gray-700/50 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                >
                  {projectCategories.map(cat => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {projectCategories.find(c => c.value === (formData.category || 'apps'))?.description}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Status *
                </label>
                <select
                  value={formData.status || 'concept'}
                  onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value as Project['status'] }))}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white/50 dark:bg-gray-700/50 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                >
                  {statusOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Completion Percentage
                </label>
                <div className="flex items-center gap-4">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={formData.completion_percentage || 0}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      completion_percentage: parseInt(e.target.value) 
                    }))}
                    className="flex-1"
                  />
                  <span className="text-lg font-medium text-gray-900 dark:text-white">
                    {formData.completion_percentage || 0}%
                  </span>
                </div>
              </div>

              {/* Stage-Specific Metrics */}
              <div className="col-span-2">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Stage-Specific Metrics
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Concept Stage Metrics */}
                  {formData.status === 'concept' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Research Hours
                        </label>
                        <input
                          type="number"
                          value={formData.metrics?.research_hours || ''}
                          onChange={(e) => setFormData(prev => ({ 
                            ...prev, 
                            metrics: { ...prev.metrics, research_hours: parseInt(e.target.value) || 0 }
                          }))}
                          className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white/50 dark:bg-gray-700/50 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Market Size
                        </label>
                        <input
                          type="text"
                          value={formData.metrics?.market_size || ''}
                          onChange={(e) => setFormData(prev => ({ 
                            ...prev, 
                            metrics: { ...prev.metrics, market_size: e.target.value }
                          }))}
                          placeholder="e.g., $50B"
                          className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white/50 dark:bg-gray-700/50 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Team Size
                        </label>
                        <input
                          type="number"
                          value={formData.metrics?.team_size || ''}
                          onChange={(e) => setFormData(prev => ({ 
                            ...prev, 
                            metrics: { ...prev.metrics, team_size: parseInt(e.target.value) || 1 }
                          }))}
                          className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white/50 dark:bg-gray-700/50 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Funding Stage
                        </label>
                        <select
                          value={formData.metrics?.funding_stage || 'Bootstrap'}
                          onChange={(e) => setFormData(prev => ({ 
                            ...prev, 
                            metrics: { ...prev.metrics, funding_stage: e.target.value }
                          }))}
                          className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white/50 dark:bg-gray-700/50 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                        >
                          <option value="Bootstrap">Bootstrap</option>
                          <option value="Pre-Seed">Pre-Seed</option>
                          <option value="Seed">Seed</option>
                          <option value="Series A">Series A</option>
                        </select>
                      </div>
                    </>
                  )}

                  {/* Prototype Stage Metrics */}
                  {formData.status === 'prototype' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Funding Stage
                        </label>
                        <select
                          value={formData.metrics?.funding_stage || 'Bootstrap'}
                          onChange={(e) => setFormData(prev => ({ 
                            ...prev, 
                            metrics: { ...prev.metrics, funding_stage: e.target.value }
                          }))}
                          className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white/50 dark:bg-gray-700/50 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                        >
                          <option value="Bootstrap">Bootstrap</option>
                          <option value="Pre-Seed">Pre-Seed</option>
                          <option value="Seed">Seed</option>
                          <option value="Series A">Series A</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          TAM
                        </label>
                        <input
                          type="text"
                          value={formData.metrics?.tam || ''}
                          onChange={(e) => setFormData(prev => ({ 
                            ...prev, 
                            metrics: { ...prev.metrics, tam: e.target.value }
                          }))}
                          placeholder="e.g., $50B"
                          className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white/50 dark:bg-gray-700/50 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Development Progress
                        </label>
                        <input
                          type="text"
                          value={formData.metrics?.development_progress || ''}
                          onChange={(e) => setFormData(prev => ({ 
                            ...prev, 
                            metrics: { ...prev.metrics, development_progress: e.target.value }
                          }))}
                          placeholder="e.g., 75%"
                          className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white/50 dark:bg-gray-700/50 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          User Feedback
                        </label>
                        <input
                          type="text"
                          value={formData.metrics?.user_feedback || ''}
                          onChange={(e) => setFormData(prev => ({ 
                            ...prev, 
                            metrics: { ...prev.metrics, user_feedback: e.target.value }
                          }))}
                          placeholder="e.g., Very positive"
                          className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white/50 dark:bg-gray-700/50 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                        />
                      </div>
                    </>
                  )}

                  {/* MVP Stage Metrics */}
                  {formData.status === 'mvp' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Beta Users
                        </label>
                        <input
                          type="number"
                          value={formData.metrics?.beta_users || ''}
                          onChange={(e) => setFormData(prev => ({ 
                            ...prev, 
                            metrics: { ...prev.metrics, beta_users: parseInt(e.target.value) || 0 }
                          }))}
                          className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white/50 dark:bg-gray-700/50 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Uptime
                        </label>
                        <input
                          type="text"
                          value={formData.metrics?.uptime || ''}
                          onChange={(e) => setFormData(prev => ({ 
                            ...prev, 
                            metrics: { ...prev.metrics, uptime: e.target.value }
                          }))}
                          placeholder="e.g., 99.9%"
                          className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white/50 dark:bg-gray-700/50 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          User Engagement
                        </label>
                        <input
                          type="text"
                          value={formData.metrics?.user_engagement || ''}
                          onChange={(e) => setFormData(prev => ({ 
                            ...prev, 
                            metrics: { ...prev.metrics, user_engagement: e.target.value }
                          }))}
                          placeholder="e.g., 85% DAU"
                          className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white/50 dark:bg-gray-700/50 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Conversion Rate
                        </label>
                        <input
                          type="text"
                          value={formData.metrics?.conversion_rate || ''}
                          onChange={(e) => setFormData(prev => ({ 
                            ...prev, 
                            metrics: { ...prev.metrics, conversion_rate: e.target.value }
                          }))}
                          placeholder="e.g., 12%"
                          className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white/50 dark:bg-gray-700/50 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                        />
                      </div>
                    </>
                  )}

                  {/* Launched Stage Metrics */}
                  {formData.status === 'launched' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Active Users
                        </label>
                        <input
                          type="number"
                          value={formData.metrics?.users || ''}
                          onChange={(e) => setFormData(prev => ({ 
                            ...prev, 
                            metrics: { ...prev.metrics, users: parseInt(e.target.value) || 0 }
                          }))}
                          className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white/50 dark:bg-gray-700/50 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Revenue Status
                        </label>
                        <select
                          value={formData.metrics?.revenue_status || 'Pre-Revenue'}
                          onChange={(e) => setFormData(prev => ({ 
                            ...prev, 
                            metrics: { ...prev.metrics, revenue_status: e.target.value }
                          }))}
                          className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white/50 dark:bg-gray-700/50 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                        >
                          <option value="Pre-Revenue">Pre-Revenue</option>
                          <option value="Generating Revenue">Generating Revenue</option>
                          <option value="Profitable">Profitable</option>
                          <option value="Break-even">Break-even</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Customer Satisfaction
                        </label>
                        <input
                          type="text"
                          value={formData.metrics?.customer_satisfaction || ''}
                          onChange={(e) => setFormData(prev => ({ 
                            ...prev, 
                            metrics: { ...prev.metrics, customer_satisfaction: e.target.value }
                          }))}
                          placeholder="e.g., 4.8/5"
                          className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white/50 dark:bg-gray-700/50 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Market Fit Score
                        </label>
                        <input
                          type="text"
                          value={formData.metrics?.market_fit || ''}
                          onChange={(e) => setFormData(prev => ({ 
                            ...prev, 
                            metrics: { ...prev.metrics, market_fit: e.target.value }
                          }))}
                          placeholder="e.g., 8.5/10"
                          className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white/50 dark:bg-gray-700/50 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                        />
                      </div>
                    </>
                  )}

                  {/* Scaling Stage Metrics */}
                  {formData.status === 'scaling' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Total Users
                        </label>
                        <input
                          type="number"
                          value={formData.metrics?.users || ''}
                          onChange={(e) => setFormData(prev => ({ 
                            ...prev, 
                            metrics: { ...prev.metrics, users: parseInt(e.target.value) || 0 }
                          }))}
                          className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white/50 dark:bg-gray-700/50 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          ARR ($)
                        </label>
                        <input
                          type="number"
                          value={formData.metrics?.arr || ''}
                          onChange={(e) => setFormData(prev => ({ 
                            ...prev, 
                            metrics: { ...prev.metrics, arr: parseInt(e.target.value) || 0 }
                          }))}
                          className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white/50 dark:bg-gray-700/50 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Team Size
                        </label>
                        <input
                          type="number"
                          value={formData.metrics?.team_size || ''}
                          onChange={(e) => setFormData(prev => ({ 
                            ...prev, 
                            metrics: { ...prev.metrics, team_size: parseInt(e.target.value) || 1 }
                          }))}
                          className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white/50 dark:bg-gray-700/50 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Market Share (%)
                        </label>
                        <input
                          type="text"
                          value={formData.metrics?.market_share || ''}
                          onChange={(e) => setFormData(prev => ({ 
                            ...prev, 
                            metrics: { ...prev.metrics, market_share: e.target.value }
                          }))}
                          placeholder="e.g., 2.5%"
                          className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white/50 dark:bg-gray-700/50 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="col-span-2">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_featured || false}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      is_featured: e.target.checked 
                    }))}
                    className="w-5 h-5 rounded text-orange-500 focus:ring-orange-500"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Feature this project
                  </span>
                  <Star className="w-5 h-5 text-yellow-500" />
                </label>
              </div>
            </div>
          </motion.div>

          {/* Media */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl border border-gray-200/50 dark:border-gray-700/50 shadow-xl p-8"
          >
            <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">
              Media & Links
            </h2>

            <div className="space-y-6">
              {/* Main Image */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Main Project Image
                </label>
                <div className="flex items-start gap-4">
                  {imagePreview && (
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-32 h-32 object-cover rounded-xl border border-gray-300 dark:border-gray-600"
                    />
                  )}
                  <div className="flex-1">
                    <input
                      ref={imageInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => imageInputRef.current?.click()}
                      className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    >
                      <Upload className="w-5 h-5" />
                      Upload Image
                    </button>
                    <p className="text-sm text-gray-500 mt-2">
                      Recommended: 1200x630px for optimal display
                    </p>
                  </div>
                </div>
              </div>

              {/* Gallery Images */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Gallery Images
                </label>
                <div className="space-y-4">
                  {galleryPreviews.length > 0 && (
                    <div className="grid grid-cols-4 gap-4">
                      {galleryPreviews.map((url, index) => (
                        <div key={index} className="relative">
                          <img
                            src={url}
                            alt={`Gallery ${index + 1}`}
                            className="w-full h-24 object-cover rounded-lg"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setGalleryPreviews(prev => prev.filter((_, i) => i !== index));
                              setFormData(prev => ({
                                ...prev,
                                gallery_images: prev.gallery_images?.filter((_, i) => i !== index)
                              }));
                            }}
                            className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <input
                    ref={galleryInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleGalleryUpload}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => galleryInputRef.current?.click()}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    <ImageIcon className="w-5 h-5" />
                    Add Gallery Images
                  </button>
                </div>
              </div>

              {/* Links */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Demo Link
                  </label>
                  <div className="relative">
                    <LinkIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="url"
                      value={formData.demo_url || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, demo_url: e.target.value }))}
                      className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white/50 dark:bg-gray-700/50 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                      placeholder="https://..."
                    />
                  </div>
                </div>

              </div>
            </div>
          </motion.div>

          {/* Technical Details */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl border border-gray-200/50 dark:border-gray-700/50 shadow-xl p-8 relative z-10"
          >
            <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">
              Technical Details
            </h2>

            <div className="space-y-6">
              {/* Tech Stack */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Tech Stack
                </label>
                <div className="space-y-3" ref={techStackRef}>
                  <div className="relative">
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          value={techStackSearch}
                          onChange={(e) => setTechStackSearch(e.target.value)}
                          onFocus={() => setShowTechStackDropdown(true)}
                          className="w-full pl-10 pr-4 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white/50 dark:bg-gray-700/50 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                          placeholder="Search tech stack..."
                        />
                      </div>
                    </div>
                    
                    {/* Tech Stack Dropdown */}
                    <AnimatePresence>
                      {showTechStackDropdown && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          className="absolute top-full mt-2 w-full bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 max-h-96 overflow-y-auto z-20"
                        >
                          <div className="p-2">
                            {['frontend', 'backend', 'database', 'devops', 'mobile', 'other'].map(category => {
                              const categoryTechs = techStackOptions
                                .filter(tech => 
                                  tech.category === category && 
                                  tech.name.toLowerCase().includes(techStackSearch.toLowerCase()) &&
                                  !formData.tech_stack?.includes(tech.name)
                                );
                              
                              if (categoryTechs.length === 0) return null;
                              
                              return (
                                <div key={category} className="mb-4">
                                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider px-3 py-1">
                                    {category}
                                  </p>
                                  <div className="grid grid-cols-2 gap-1">
                                    {categoryTechs.map(tech => (
                                      <button
                                        key={tech.id}
                                        type="button"
                                        onClick={() => addTechStack(tech.name)}
                                        className="flex items-center gap-2 px-3 py-2 hover:bg-blue-50 dark:hover:bg-blue-900/30 hover:text-blue-700 dark:hover:text-blue-300 rounded-lg transition-all duration-200 text-left group"
                                      >
                                        <TechStackIcon 
                                          tech={tech.name} 
                                          size={20} 
                                          className="text-gray-600 dark:text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors" 
                                        />
                                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-blue-700 dark:group-hover:text-blue-300 transition-colors">{tech.name}</span>
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    {formData.tech_stack?.map((tech, index) => (
                        <motion.span
                          key={index}
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="px-3 py-1.5 bg-gradient-to-r from-blue-100 to-blue-200 dark:from-blue-900/40 dark:to-blue-800/40 text-blue-800 dark:text-blue-200 rounded-full text-sm flex items-center gap-2 shadow-sm border border-blue-200 dark:border-blue-700"
                        >
                          <TechStackIcon 
                            tech={tech} 
                            size={16} 
                            className="text-blue-600 dark:text-blue-400" 
                          />
                          <span className="font-medium">{tech}</span>
                          <button
                            type="button"
                            onClick={() => removeTechStack(index)}
                            className="text-blue-600 hover:text-red-600 dark:text-blue-400 dark:hover:text-red-400 transition-colors"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </motion.span>
                    ))}
                  </div>
                  
                  {formData.tech_stack && formData.tech_stack.length > 0 && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {formData.tech_stack.length} technologies selected
                    </p>
                  )}
                </div>
              </div>

              {/* Key Features */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Key Features
                </label>
                <div className="space-y-3">
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newFeature.name}
                        onChange={(e) => setNewFeature(prev => ({ ...prev, name: e.target.value }))}
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                        className="flex-1 px-4 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white/50 dark:bg-gray-700/50 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                        placeholder="Feature name"
                      />
                      <select
                        value={newFeature.status}
                        onChange={(e) => setNewFeature(prev => ({ ...prev, status: e.target.value }))}
                        className="px-4 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white/50 dark:bg-gray-700/50 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                      >
                        <option value="planned">Planned</option>
                        <option value="in-progress">In Progress</option>
                        <option value="completed">Completed</option>
                      </select>
                      <button
                        type="button"
                        onClick={addFeature}
                        className="px-4 py-2 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition-colors"
                      >
                        <Plus className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {formData.key_features?.map((feature, index) => {
                      // Ensure we have the correct feature object structure
                      const featureObj = typeof feature === 'string' 
                        ? { name: feature, status: 'completed' as const, description: '' }
                        : feature;
                      
                      const getStatusIcon = () => {
                        switch (featureObj.status) {
                          case 'completed':
                            return <CheckCircle className="w-5 h-5 text-green-500" />;
                          case 'in-progress':
                            return <Clock className="w-5 h-5 text-blue-500" />;
                          case 'planned':
                            return <Target className="w-5 h-5 text-gray-500" />;
                          default:
                            return <CheckCircle className="w-5 h-5 text-green-500" />;
                        }
                      };
                      
                      return (
                        <div key={index} className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                          {getStatusIcon()}
                          <span className="flex-1">{featureObj.name}</span>
                          <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                            {featureObj.status?.replace('-', ' ') || 'completed'}
                          </span>
                          <button
                            type="button"
                            onClick={() => removeFeature(index)}
                            className="text-gray-500 hover:text-red-500"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* SEO & Metadata */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl border border-gray-200/50 dark:border-gray-700/50 shadow-xl p-8"
          >
            <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">
              SEO & Metadata
            </h2>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Meta Title
                </label>
                <input
                  type="text"
                  value={formData.meta_title || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, meta_title: e.target.value }))}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white/50 dark:bg-gray-700/50 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                  placeholder="SEO optimized title (50-60 characters)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Meta Description
                </label>
                <textarea
                  value={formData.meta_description || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, meta_description: e.target.value }))}
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white/50 dark:bg-gray-700/50 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all resize-none"
                  placeholder="SEO description (150-160 characters)"
                />
              </div>

            </div>
          </motion.div>

        </form>
      </div>
    </motion.div>
  );
}
